import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import ArrowBack from '@material-ui/icons/ArrowBack';
import Alert from '@material-ui/lab/Alert';
import { Formik } from 'formik';
import { FormattedMessage } from 'react-intl';
import SmallCenteredPaper from 'components/templates/SmallCenteredPaper';
import { Input } from 'components/FormInputs';

const RenderForm = ({
  values,
  errors,
  isSubmitting,
  isValid,
  handleChange,
  handleSubmit,
  dirty,
}) => (
  <form onSubmit={handleSubmit} autoComplete="new-password">
    <Input
      name="password"
      onChange={handleChange}
      errors={errors}
      value={values.password}
      type="password"
      label={<FormattedMessage id="changepwd.newpassword" defaultMessage="New password" />}
    />
    <Input
      name="password_confirm"
      onChange={handleChange}
      errors={errors}
      value={values.password_confirm}
      type="password"
      label={
        <FormattedMessage id="changepwd.confirmpassword" defaultMessage="Confirm new password" />
      }
    />
    <Box mt={2}>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        disabled={!isValid || isSubmitting || !dirty}
      >
        <FormattedMessage id="userform.changePassword" defaultMessage="Change password" />
      </Button>
    </Box>
  </form>
);

RenderForm.propTypes = {
  values: PropTypes.shape({
    password: PropTypes.string,
    password_confirm: PropTypes.string,
  }).isRequired,
  errors: PropTypes.shape({}).isRequired,
  isSubmitting: PropTypes.bool,
  isValid: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dirty: PropTypes.bool,
};

const ResetPassword = ({ intl, onSubmit, success }) => {
  if (success) {
    return (
      <SmallCenteredPaper
        after={
          <Button component={Link} to="/login">
            <ArrowBack />
            <FormattedMessage id="welcome.backTo" defaultMessage="Back to the main page" />
          </Button>
        }
      >
        <Alert severity="success" elevation={3}>
          <FormattedMessage
            id="passwordreset.success"
            defaultMessage="Your password has been restored. You can now login."
          />
        </Alert>
      </SmallCenteredPaper>
    );
  }

  return (
    <SmallCenteredPaper
      after={
        <Button component={Link} to="/login">
          <ArrowBack />
          <FormattedMessage id="welcome.backTo" defaultMessage="Back to the main page" />
        </Button>
      }
    >
      <Typography variant="h6">
        <FormattedMessage id="passwordreset.info" defaultMessage="Select your new password" />
      </Typography>
      <Formik
        initialValues={{
          password: '',
          password_confirm: '',
        }}
        validateOnChange
        validate={(values) => {
          const err = {};

          ['password', 'password_confirm'].forEach((f) => {
            if (!values[f]) {
              err[f] = intl.formatMessage({
                id: 'Required',
                defaultMessage: 'Required',
              });
            }

            if (values[f] && values[f].length < 6) {
              err[f] = intl.formatMessage({
                id: 'userForm.min6CharsPassword',
                defaultMessage: 'Password should have at least 6 characters',
              });
            }
          });

          if (
            values.password_confirm &&
            values.password &&
            values.password_confirm !== values.password
          ) {
            err.password_confirm = intl.formatMessage({
              id: 'userForm.passwordMismatch',
              defaultMessage: "Password doesn't match",
            });
          }

          return err;
        }}
        enableReinitialize
        onSubmit={async (values, { setSubmitting }) => {
          try {
            setSubmitting(true);

            await onSubmit(values);
          } catch (err) {
            setSubmitting(false);
          }
        }}
        render={(fp) => <RenderForm {...fp} />}
      />
    </SmallCenteredPaper>
  );
};

ResetPassword.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  success: PropTypes.bool.isRequired,
};

export default ResetPassword;
